import React, { useState, useEffect } from 'react'
import { Calculator, Eye, EyeOff, Lightbulb } from 'lucide-react'
import katex from 'katex'

interface FormulaVariable {
  symbol: string
  name: string
  unit: string
  min: number
  max: number
  default: number
  step?: number
}

interface InteractiveFormulaProps {
  title: string
  formula: string
  variables: FormulaVariable[]
  resultFormula: string
  resultUnit: string
  description?: string
  insights?: string[]
  onCalculate?: (result: number, variables: Record<string, number>) => void
}

const InteractiveFormula: React.FC<InteractiveFormulaProps> = ({
  title,
  formula,
  variables,
  resultFormula,
  resultUnit,
  description,
  insights = [],
  onCalculate
}) => {
  const [values, setValues] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {}
    variables.forEach(variable => {
      initial[variable.symbol] = variable.default
    })
    return initial
  })
  
  const [result, setResult] = useState<number>(0)
  const [showInsights, setShowInsights] = useState(false)
  const [showDerivation, setShowDerivation] = useState(false)

  // Calculate result whenever values change
  useEffect(() => {
    try {
      // Replace variables in formula with actual values
      let calculationFormula = resultFormula
      variables.forEach(variable => {
        const regex = new RegExp(variable.symbol, 'g')
        calculationFormula = calculationFormula.replace(regex, values[variable.symbol].toString())
      })
      
      // Evaluate the formula (simple math expressions)
      const calculatedResult = evaluateFormula(calculationFormula)
      setResult(calculatedResult)
      onCalculate?.(calculatedResult, values)
    } catch (error) {
      console.error('Formula calculation error:', error)
      setResult(0)
    }
  }, [values, resultFormula, variables, onCalculate])

  const evaluateFormula = (formula: string): number => {
    // Simple formula evaluator - replace with more robust solution if needed
    try {
      // Replace common math functions
      let evalFormula = formula
        .replace(/sin/g, 'Math.sin')
        .replace(/cos/g, 'Math.cos')
        .replace(/tan/g, 'Math.tan')
        .replace(/sqrt/g, 'Math.sqrt')
        .replace(/pi/g, 'Math.PI')
        .replace(/\^/g, '**')
      
      // Use Function constructor for safer evaluation
      return new Function('return ' + evalFormula)()
    } catch {
      return 0
    }
  }

  const handleVariableChange = (symbol: string, value: number) => {
    setValues(prev => ({
      ...prev,
      [symbol]: value
    }))
  }

  const renderFormula = (formulaString: string) => {
    try {
      return katex.renderToString(formulaString, {
        displayMode: true,
        throwOnError: false
      })
    } catch {
      return formulaString
    }
  }

  const getInsightForCurrentValues = () => {
    // Simple insight logic based on result magnitude
    if (result === 0) return "Kết quả bằng 0 - kiểm tra các giá trị đầu vào"
    if (Math.abs(result) < 0.001) return "Giá trị rất nhỏ - có thể cần điều chỉnh đơn vị"
    if (Math.abs(result) > 1000) return "Giá trị lớn - kiểm tra tính hợp lý của kết quả"
    return "Giá trị trong khoảng hợp lý"
  }

  return (
    <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border border-green-200 p-6 my-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <Calculator className="w-6 h-6 text-green-600" />
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          </div>
          {description && (
            <p className="text-gray-700 text-sm">{description}</p>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDerivation(!showDerivation)}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-white"
            title="Hiển thị công thức"
          >
            {showDerivation ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
          
          <button
            onClick={() => setShowInsights(!showInsights)}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-white"
            title="Hiển thị gợi ý"
          >
            <Lightbulb className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Formula Display */}
      {showDerivation && (
        <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3">Công thức:</h4>
          <div 
            className="text-center mb-3"
            dangerouslySetInnerHTML={{ __html: renderFormula(formula) }}
          />
          <div className="text-sm text-gray-600">
            <p className="mb-2">Trong đó:</p>
            <ul className="space-y-1">
              {variables.map(variable => (
                <li key={variable.symbol} className="flex items-center space-x-2">
                  <span className="font-mono font-semibold">{variable.symbol}:</span>
                  <span>{variable.name} ({variable.unit})</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Variable Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {variables.map(variable => (
          <div key={variable.symbol} className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-gray-900">
                {variable.symbol} - {variable.name}
              </label>
              <span className="text-sm text-gray-500">{variable.unit}</span>
            </div>
            
            <div className="space-y-2">
              <input
                type="range"
                min={variable.min}
                max={variable.max}
                step={variable.step || 0.1}
                value={values[variable.symbol]}
                onChange={(e) => handleVariableChange(variable.symbol, Number(e.target.value))}
                className="w-full"
              />
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{variable.min}</span>
                <input
                  type="number"
                  min={variable.min}
                  max={variable.max}
                  step={variable.step || 0.1}
                  value={values[variable.symbol]}
                  onChange={(e) => handleVariableChange(variable.symbol, Number(e.target.value))}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                />
                <span className="text-gray-500">{variable.max}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Result Display */}
      <div className="bg-white rounded-lg p-6 border-2 border-blue-300 mb-4">
        <div className="text-center">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Kết quả</h4>
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {result.toFixed(3)} {resultUnit}
          </div>
          <div className="text-sm text-gray-600">
            {getInsightForCurrentValues()}
          </div>
        </div>
      </div>

      {/* Insights */}
      {showInsights && insights.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
            <Lightbulb className="w-4 h-4 mr-2" />
            Gợi ý và nhận xét
          </h4>
          <ul className="space-y-2 text-sm text-yellow-800">
            {insights.map((insight, index) => (
              <li key={index} className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-0.5">•</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Current Values Summary */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        Giá trị hiện tại: {variables.map(v => `${v.symbol}=${values[v.symbol]}`).join(', ')}
      </div>
    </div>
  )
}

export default InteractiveFormula